import React, { useState, useRef } from 'react';
import { useHRPersonnelList } from '../../hooks/useHRQueries';
import LoadingSpinner from '../ui/LoadingSpinner';
import { formatNumber } from '../../utils/formatters';
import ChartControls from '../ui/ChartControls';
import useChartGuide from '../../hooks/useChartGuide';
import { useFavoriteToggle } from '../../hooks/useFavoriteToggle';
import { CHART_GUIDES } from '../../constants/chartGuides';

interface PersonnelPerformanceByRoleProps {
  branchId?: number;
  showLoading?: boolean;
  onError?: (error: Error) => void;
}

const PerformanceMetricsHint: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsVisible(!isVisible)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="inline-flex items-center justify-center w-5 h-5 rounded text-text-tertiary hover:text-text-secondary transition-colors duration-200"
        aria-label="اطلاعات معیارهای عملکرد کلی"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </button>

      {isVisible && (
        <div className="absolute z-50 left-0 top-7 w-72 p-4 bg-bg-primary dark:bg-gray-800 rounded-lg shadow-lg border border-border-secondary dark:border-gray-700"
          style={{
            animation: 'fadeInScale 0.15s ease-out',
            transformOrigin: 'top center'
          }}>
          <h4 className="text-sm font-medium text-text-primary mb-3">
            معیارهای عملکرد کلی
          </h4>
          
          <div className="space-y-3 text-xs text-text-secondary">
            <div className="flex items-start gap-2">
              <span className="text-green-500 mt-0.5">🌟</span>
              <div>
                <span className="font-medium text-text-primary">عالی:</span>
                <span className="mr-1">رضایت مشتریان بالای ۹۰%</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-yellow-500 mt-0.5">👍</span>
              <div>
                <span className="font-medium text-text-primary">خوب:</span>
                <span className="mr-1">رضایت مشتریان بین ۸۰% تا ۹۰%</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-red-500 mt-0.5">⚠️</span>
              <div>
                <span className="font-medium text-text-primary">نیاز به بهبود:</span>
                <span className="mr-1">رضایت مشتریان کمتر از 80%</span>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-gray-500 mt-0.5">📝</span>
              <div>
                <span className="font-medium text-text-primary">عدم ثبت نظر:</span>
                <span className="mr-1">هیچ نظری از مشتریان ثبت نشده است</span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border-secondary">
              <p className="text-xs text-text-tertiary">
                عملکرد کلی بر اساس درصد رضایت مشتریان (امتیاز عالی و خوب از کل نظرات) محاسبه می‌شود.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PersonnelPerformanceByRole: React.FC<PersonnelPerformanceByRoleProps> = ({ 
  branchId,
  showLoading = true,
  onError 
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<string>('cashier');
  const { data: allData, isLoading, error } = useHRPersonnelList(branchId);
  const { openGuide } = useChartGuide();
  const { handleFavoriteToggle, isFavorited } = useFavoriteToggle();
  
  // Component metadata
  const componentId = 'personnel-performance-by-role';
  const componentTitle = 'عملکرد پرسنل به تفکیک نقش';

  React.useEffect(() => {
    if (error && onError) {
      onError(error as Error);
    }
  }, [error, onError]);

  if (isLoading && showLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <LoadingSpinner variant="minimal" size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-base border border-status-error p-4">
        <p className="text-status-error">خطا در بارگذاری عملکرد پرسنل</p>
      </div>
    );
  }

  if (!allData) {
    return null;
  }

  const roleNames = {
    cashier: 'صندوقداران',
    courier: 'پیک‌ها',
    host: 'میزبان‌ها'
  };

  return (
    <div ref={chartRef}>
      <div className="mb-6">
        <ChartControls
          componentId={componentId}
          componentTitle={componentTitle}
          componentType="table"
          dashboardSource="hr"
          onInfo={() => openGuide(CHART_GUIDES.personnelPerformanceByRole)}
          onFavorite={() => handleFavoriteToggle({
            componentId,
            componentTitle,
            dashboardSource: 'hr',
            componentType: 'table',
            description: 'عملکرد جزئی پرسنل بر اساس نقش هر یک',
            props: { branchId, showLoading, onError },
            gridLayout: {
              containerClass: 'grid grid-cols-1 gap-6'
            }
          })}
          isFavorited={isFavorited(componentId)}
          chartRef={chartRef}
          className="mb-4"
        />
        <h2 className="title-section">{componentTitle}</h2>
      </div>
      
      <div className="card-base card-padding animate-enter animate-delay-3">
        {/* Tab Navigation */}
        <div className="border-b border-border-secondary">
          <div className="flex flex-wrap items-center justify-center -mb-px">
            {['cashier', 'courier', 'host'].map((role, index) => {
              const roleData = allData.filter((staff: any) => staff.role === role);
              
              return (
                <button
                  key={role}
                  onClick={() => setActiveTab(role)}
                  className={`
                    relative flex items-center justify-center gap-3 px-8 py-2.5 text-base font-medium transition-all duration-300
                    ${index > 0 ? 'mr-2' : ''}
                    ${activeTab === role
                      ? 'bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 border-t-2 border-r-2 border-l-2 border-border-secondary rounded-t-xl shadow-[0_-8px_16px_rgba(0,0,0,0.15),-6px_0_12px_rgba(0,0,0,0.08),6px_0_12px_rgba(0,0,0,0.08)]'
                      : 'bg-gray-50 dark:bg-gray-900 text-text-secondary hover:text-text-primary hover:bg-gray-100 dark:hover:bg-gray-800 rounded-t-xl'
                    }
                  `}
                >
                  <span className="text-lg font-semibold">{roleNames[role as keyof typeof roleNames]}</span>
                  <span className={`
                    px-2.5 py-0.5 text-sm font-bold rounded-full
                    ${activeTab === role
                      ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                      : 'bg-gray-200 dark:bg-gray-700 text-text-tertiary'
                    }
                  `}>
                    {roleData.length}
                  </span>
                  {activeTab === role && (
                    <div className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-white dark:bg-gray-800"></div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="animate-enter stagger-5">
          {['cashier', 'courier', 'host'].map((role) => {
            if (activeTab !== role) return null;

            const roleData = allData
              .filter((staff: any) => staff.role === role)
              .sort((a: any, b: any) => b.satisfaction - a.satisfaction);
            const staffWithQuestions = roleData.filter((staff: any) => staff.countQuestion !== 0);
            const totalQuestions = staffWithQuestions.reduce((acc: number, staff: any) => acc + staff.countQuestion, 0);
            const totalGoodQuestions = staffWithQuestions.reduce((acc: number, staff: any) => acc + staff.countQuestionGood, 0);
            
            const avgSatisfaction = totalQuestions > 0
              ? Math.round((totalGoodQuestions / totalQuestions) * 100)
              : 0;

            return (
              <div key={role}>
                {/* Statistics Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  <div className="bg-bg-secondary rounded-lg p-4 text-center">
                    <p className="text-text-secondary text-sm mb-1">تعداد پرسنل</p>
                    <p className="text-2xl font-bold text-text-primary">{roleData.length}</p>
                  </div>
                  <div className="bg-bg-secondary rounded-lg p-4 text-center">
                    <p className="text-text-secondary text-sm mb-1">میانگین رضایت</p>
                    <p className="text-2xl font-bold text-text-primary">{avgSatisfaction}%</p>
                  </div>
                  <div className="bg-bg-secondary rounded-lg p-4 text-center">
                    <p className="text-text-secondary text-sm mb-1">عملکرد برتر</p>
                    <p className="text-2xl font-bold text-status-success">{roleData.filter((s: any) => s.countQuestion !== 0 && s.satisfaction >= 90).length}</p>
                  </div>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-lg border border-border-secondary">
                  <div className="bg-bg-primary border-b border-border-secondary">
                    <table className="min-w-full divide-y divide-border-secondary" style={{ direction: 'rtl', tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th className="px-6 py-3 text-right text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: role === 'courier' ? '15%' : '12%' }}>نام پرسنل</th>
                          <th className="px-6 py-3 text-center text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: '10%' }}>عالی</th>
                          <th className="px-6 py-3 text-center text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: '10%' }}>خوب</th>
                          <th className="px-6 py-3 text-center text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: '10%' }}>متوسط</th>
                          <th className="px-6 py-3 text-center text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: '10%' }}>ضعیف</th>
                          <th className="px-6 py-3 text-center text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: role === 'courier' ? '12%' : '10%' }}>رضایت مشتریان</th>
                          <th className="px-6 py-3 text-center text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: role === 'courier' ? '12%' : '10%' }}>تعداد سفارش</th>
                          {(role === 'cashier' || role === 'host') && (
                            <th className="px-6 py-3 text-center text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: '10%' }}>درصد اشتراک‌گیری</th>
                          )}
                          <th className="px-6 py-3 text-center text-xs font-bold text-text-primary uppercase tracking-wider" style={{ direction: 'rtl', width: role === 'courier' ? '12%' : '10%' }}>
                            <div className="flex items-center justify-center gap-1">
                              <span>عملکرد کلی</span>
                              <PerformanceMetricsHint />
                            </div>
                          </th>
                        </tr>
                      </thead>
                    </table>
                  </div>

                  <div className="overflow-y-auto scrollbar-thin scrollbar-thumb-border-muted scrollbar-track-transparent" style={{ maxHeight: '400px' }}>
                    <table className="min-w-full divide-y divide-border-secondary" style={{ direction: 'rtl', tableLayout: 'fixed' }}>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {roleData.map((row: any, rowIndex: number) => {
                          // Calculate subscription percentage for cashier and host roles
                          // console.log(row)
                          const subscriptionRate = (role === 'cashier' || role === 'host') 
                            ? row.subscriptionRate
                            : 0;
                          
                          return (
                            <tr key={rowIndex} className={`${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'} hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors`} style={{ height: '65px' }}>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-text-primary" style={{ direction: 'rtl', width: role === 'courier' ? '15%' : '12%' }}>
                                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                                  <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center text-white text-xs font-bold shadow-sm">
                                    {row.name.charAt(0)}
                                  </div>
                                  <span className="font-medium text-text-primary group-hover:text-chart-color-1 transition-colors text-xs">
                                    {row.name}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-xs font-bold text-text-primary text-center" style={{ direction: 'rtl', width: '10%' }}>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                  {formatNumber(row.best || 0)}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-xs font-bold text-text-primary text-center" style={{ direction: 'rtl', width: '10%' }}>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                  {formatNumber(row.good || 0)}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-xs font-bold text-text-primary text-center" style={{ direction: 'rtl', width: '10%' }}>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                                  {formatNumber(row.med || 0)}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-xs font-bold text-text-primary text-center" style={{ direction: 'rtl', width: '10%' }}>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                                  {formatNumber(row.bad || 0)}
                                </span>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-text-primary text-center" style={{ direction: 'rtl', width: role === 'courier' ? '12%' : '10%' }}>
                                {row.countQuestion === 0 ? (
                                  <span className="inline-flex items-center px-2 py-1 text-xs text-gray-500 dark:text-gray-400">
                                    -
                                  </span>
                                ) : (
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${row.positiveReviews >= 90
                                    ? 'bg-status-success text-text-inverse'
                                    : row.positiveReviews >= 80
                                      ? 'bg-status-warning text-text-inverse'
                                      : 'bg-status-error text-text-inverse'
                                    }`}>
                                    {row.positiveReviews}%
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-xs font-bold text-text-primary text-center" style={{ direction: 'rtl', width: role === 'courier' ? '12%' : '10%' }}>
                                {formatNumber(row.invoices)}
                              </td>
                              {(role === 'cashier' || role === 'host') && (
                                <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-text-primary text-center" style={{ direction: 'rtl', width: '10%' }}>
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${subscriptionRate >= 70
                                    ? 'bg-status-success text-text-inverse'
                                    : subscriptionRate >= 50
                                      ? 'bg-status-warning text-text-inverse'
                                      : 'bg-status-error text-text-inverse'
                                    }`}>
                                    {subscriptionRate}%
                                  </span>
                                </td>
                              )}
                              <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-text-primary text-center" style={{ direction: 'rtl', width: role === 'courier' ? '12%' : '10%' }}>
                                {row.countQuestion === 0 ? (
                                  <span className="inline-flex items-center text-gray-500 dark:text-gray-400 font-medium text-xs">
                                    <span className="text-sm ml-1">📝</span> عدم ثبت نظر
                                  </span>
                                ) : row.satisfaction >= 90 ? (
                                  <span className="inline-flex items-center text-green-600 dark:text-green-400 font-medium text-xs">
                                    <span className="text-sm ml-1">🌟</span> عالی
                                  </span>
                                ) : row.satisfaction >= 80 ? (
                                  <span className="inline-flex items-center text-yellow-600 dark:text-yellow-400 font-medium text-xs">
                                    <span className="text-sm ml-1">👍</span> خوب
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center text-red-600 dark:text-red-400 font-medium text-xs">
                                    <span className="text-sm ml-1">⚠️</span> نیاز به بهبود
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {roleData.length === 0 && (
                          <tr>
                            <td colSpan={role === 'courier' ? 8 : 9} className="px-6 py-8 text-center text-text-secondary">
                              داده‌ای برای {roleNames[role as keyof typeof roleNames]} یافت نشد
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {roleData.length > 6 && (
                    <div className="flex items-center justify-between px-6 py-3 bg-bg-secondary border-t border-border-secondary text-sm text-text-secondary">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                        <span>برای مشاهده بیشتر اسکرول کنید</span>
                      </div>
                      <div className="flex items-center space-x-1 rtl:space-x-reverse">
                        <span className="text-text-secondary">{roleData.length} آیتم</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PersonnelPerformanceByRole;